import { TContentDomain } from '@/types/Content.types';

// Local editor model. `imageUrl` is the persisted image (edit mode); `imageFile`
// is a newly-picked file pending upload after save. `text` holds markdown.
export type TArticleSubsectionDraft = {
  _id: string;
  title: string;
  text: string;
  imageUrl?: string | null;
  imageFile?: File | null;
};

export type TArticleSectionDraft = {
  _id: string;
  title: string;
  text: string;
  imageUrl?: string | null;
  imageFile?: File | null;
  subsections: TArticleSubsectionDraft[];
};

export type TVideoSource = 'mux' | 'youtube';

export type TContentFormValues = {
  title: string;
  description: string;
  domains: TContentDomain[];
  subdomains: string[];
  // true = visible to all organizations; false = the creator's organization only.
  isPublic: boolean;
  // post (markdown)
  body: string;
  // article
  sections: TArticleSectionDraft[];
  // video (reel)
  videoSource: TVideoSource;
  videoFile?: File | null;
  videoProcessingError?: string | null;
  youtubeUrl: string;
  fullDescription: string;
  // Existing Mux state (edit mode) — drives publish gating + preview playback.
  muxStatus?: string | null;
  muxPlaybackId?: string | null;
  // Immutable edit-mode snapshot used to distinguish an existing remote video
  // from a replacement selected in the editable controls.
  originalVideoSource?: TVideoSource | null;
  originalYoutubeUrl?: string;
  hasOriginalRemoteVideo?: boolean;
};

export type TContentFormSubmit = {
  values: TContentFormValues;
  thumbnailFile: File | null;
};

export type TContentFormErrors = Partial<Record<string, string>>;

export type TContentFormMode = 'draft' | 'publish';

// A terminal wizard action (publish / save-draft).
export type WizardFinalAction = {
  key: string;
  label: string;
  mode: TContentFormMode;
  backgroundColor?: string;
  isTransparent?: boolean;
};

// One pending image upload, resolved after the content row is saved.
export type TPendingImageUpload = {
  sectionId: string;
  subsectionId?: string;
  file: File;
};

export const makeEmptySubsection = (): TArticleSubsectionDraft => ({
  _id: crypto.randomUUID(),
  title: '',
  text: '',
  imageUrl: null,
  imageFile: null,
});

export const makeEmptySection = (): TArticleSectionDraft => ({
  _id: crypto.randomUUID(),
  title: '',
  text: '',
  imageUrl: null,
  imageFile: null,
  subsections: [],
});
